import React, { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { CompanyCard, Navbar, Footer, PaginationList } from '../../components'
import { FiSearch, FiFilter, FiX } from 'react-icons/fi'
import './AllCompanies.scss'
import { companyService } from '../../services/companyService'
import { Container } from 'react-bootstrap'

const AllCompanies = () => {
  const location = useLocation()
  const navigate = useNavigate()

  const queryParams = new URLSearchParams(location.search)
  const currentPage = parseInt(queryParams.get('page')) || 1
  const qParam = queryParams.get('q') || ''
  const cityParam = queryParams.get('grad') || ''
  const typesParam = queryParams.get('tip') || ''

  const cities = useSelector(state => state.utility?.cities ?? [])
  const [companies, setCompanies] = useState([])
  const [loading, setLoading] = useState(false)
  const [searchInput, setSearchInput] = useState(qParam)
  const [totalPages, setTotalPages] = useState(1)
  const [showFilters, setShowFilters] = useState(false)

  const typeOptions = [
    'Distribucija',
    'Servis',
    'Proizvodnja',
    'Proizvodnja po narudzbi',
  ]

  const selectedTypes = typesParam ? typesParam.split(',') : []

  const perPage = 16

  useEffect(() => {
    setSearchInput(qParam)
  }, [qParam])

  useEffect(() => {
    fetchCompanies()
  }, [currentPage, qParam, cityParam, typesParam])

  const fetchCompanies = async () => {
    setLoading(true)
    const typeString = selectedTypes.length > 0 ? selectedTypes.join(',') : null

    const result = await companyService.getAllCompanies({
      page: currentPage,
      perPage,
      search: qParam,
      city: cityParam || null,
      type: typeString,
    })

    if (result.isOk) {
      setCompanies(result.data.data || [])
      setTotalPages(result.data.last_page || 1)
    }
    setLoading(false)
  }

  const handleSearch = () => {
    if (searchInput.trim()) {
      queryParams.set('q', searchInput.trim())
    } else {
      queryParams.delete('q')
    }
    queryParams.set('page', 1)
    navigate(`?${queryParams.toString()}`)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleSearchKeyPress = e => {
    if (e.key === 'Enter') {
      handleSearch()
    }
  }

  const handleCityChange = city => {
    if (city) {
      queryParams.set('grad', city)
    } else {
      queryParams.delete('grad')
    }
    queryParams.set('page', 1)
    navigate(`?${queryParams.toString()}`)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleTypeToggle = type => {
    let newTypes = [...selectedTypes]

    if (newTypes.includes(type)) {
      newTypes = newTypes.filter(t => t !== type)
    } else {
      newTypes.push(type)
    }

    if (newTypes.length > 0) {
      queryParams.set('tip', newTypes.join(','))
    } else {
      queryParams.delete('tip')
    }
    queryParams.set('page', 1)
    navigate(`?${queryParams.toString()}`)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const clearFilters = () => {
    queryParams.delete('grad')
    queryParams.delete('tip')
    queryParams.delete('q')
    queryParams.set('page', 1)
    setSearchInput('')
    navigate(`?${queryParams.toString()}`)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handlePageChange = page => {
    queryParams.set('page', page)
    navigate(`?${queryParams.toString()}`)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const hasActiveFilters = cityParam || selectedTypes.length > 0 || qParam

  return (
    <>
      <Navbar />
      <Container className="mt-5">
        <div id="companies-list">
          <div className="all-companies-container">
            <div className="page-header">
              <h1>Sve firme</h1>
              <p className="page-subtitle">
                Pronađite firme koje odgovaraju vašim potrebama
              </p>
            </div>

            <div className="search-filter-section">
              <div className="search-bar">
                <FiSearch className="search-icon" />
                <input
                  type="text"
                  placeholder="Pretražite kompanije..."
                  value={searchInput}
                  onChange={e => setSearchInput(e.target.value)}
                  onKeyPress={handleSearchKeyPress}
                  className="search-input"
                />
                <button onClick={handleSearch} className="search-button">
                  Pretraži
                </button>
              </div>

              <button
                className={`filter-toggle-btn ${showFilters ? 'active' : ''}`}
                onClick={() => setShowFilters(!showFilters)}>
                <FiFilter />
                <span>Filteri</span>
                {hasActiveFilters && (
                  <span className="filter-badge">
                    {(cityParam ? 1 : 0) + selectedTypes.length}
                  </span>
                )}
              </button>
            </div>

            {showFilters && (
              <div className="filters-panel">
                <div className="filters-header">
                  <h3>Filteri</h3>
                  {hasActiveFilters && (
                    <button
                      className="clear-filters-btn"
                      onClick={clearFilters}>
                      <FiX /> Obriši filtere
                    </button>
                  )}
                </div>

                <div className="filters-content">
                  <div className="filter-group">
                    <label className="filter-label">Grad</label>
                    <select
                      value={cityParam}
                      onChange={e => handleCityChange(e.target.value)}
                      className="filter-select">
                      <option value="">Svi gradovi</option>
                      {cities.map(city => (
                        <option key={city} value={city}>
                          {city}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="filter-group">
                    <label className="filter-label">Tip kompanije</label>
                    <div className="checkbox-group">
                      {typeOptions.map(type => (
                        <label key={type} className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={selectedTypes.includes(type)}
                            onChange={() => handleTypeToggle(type)}
                            className="checkbox-input"
                          />
                          <span className="checkbox-text">{type}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {loading ? (
              <div className="loading-container">
                <div className="loader"></div>
                <p>Učitavanje...</p>
              </div>
            ) : companies.length > 0 ? (
              <>
                <div className="companies-grid">
                  {companies.map(company => (
                    <CompanyCard key={company.id} company={company} />
                  ))}
                </div>

                {totalPages > 1 && (
                  <div className="mt-4 d-flex justify-content-center">
                    <PaginationList
                      currentPage={currentPage}
                      handlePageChange={handlePageChange}
                      totalPages={totalPages}
                    />
                  </div>
                )}
              </>
            ) : (
              <div className="no-results">
                <p>Nisu pronađene kompanije</p>
                {hasActiveFilters && (
                  <button onClick={clearFilters} className="clear-btn">
                    Obriši filtere
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </Container>
      <Footer />
    </>
  )
}

export default AllCompanies
